/**
 * SiteCountyField — Phase 20 / DEC-310 PR 2 (task 20.4 AC-2).
 *
 * The county on a site's detail screen. It shows the county with the source in
 * effect — "Typed" (a person's word, never overwritten by GPS) or "Filled from
 * GPS" (derived from the pin / linked wells, OK and TX only) — and lets a
 * DISPATCH_MANAGER+ type one, change it, or clear it back to the derived value.
 *
 * `hauler_locations` has no state column, so a typed county travels with its
 * two-letter state; the State input is pre-filled from the current
 * `countyState` (for a derived county that is the state the GPS lookup found).
 * Casing is the server's job (DEC-147): the county is sent as typed, and the
 * CSS uppercase on the input is only a hint.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { useRenameTankBattery } from './hooks';
import { fieldStyle } from './form-bits';
import { Pill, subText } from './ui-bits';

export interface SiteCountyFieldProps {
  readonly siteId: string;
  readonly county: string | null;
  readonly countyState: string | null;
  readonly countySource: 'USER' | 'DERIVED' | null;
  readonly canManage: boolean;
}

const linkButton: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-accent-blue)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

const STATE_ERROR = "Pick the county's state (two letters)";

export function SiteCountyField({
  siteId,
  county,
  countyState,
  countySource,
  canManage,
}: SiteCountyFieldProps): React.JSX.Element {
  const update = useRenameTankBattery(siteId);
  const [editing, setEditing] = React.useState(false);
  const [countyInput, setCountyInput] = React.useState('');
  const [stateInput, setStateInput] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const startEdit = (): void => {
    setCountyInput(county ?? '');
    setStateInput(countyState ?? '');
    setError(null);
    setEditing(true);
  };

  const clearTyped = (): void =>
    update.mutate(
      { county: null },
      {
        onSuccess: () => {
          toast.success('County cleared — filled from GPS where available');
          setEditing(false);
        },
        onError: () => toast.error('Could not save the county'),
      },
    );

  const save = (): void => {
    const typed = countyInput.trim();
    if (typed === '') {
      // An emptied county hands the site back to the derived value.
      clearTyped();
      return;
    }
    const state = stateInput.trim();
    if (!/^[A-Za-z]{2}$/.test(state)) {
      setError(STATE_ERROR);
      return;
    }
    setError(null);
    update.mutate(
      { county: typed, countyState: state.toUpperCase() },
      {
        onSuccess: () => {
          toast.success('County saved');
          setEditing(false);
        },
        onError: () => toast.error('Could not save the county'),
      },
    );
  };

  if (editing) {
    return (
      <div style={{ margin: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <input
            aria-label="County"
            value={countyInput}
            maxLength={60}
            onChange={(e): void => setCountyInput(e.target.value)}
            placeholder="County"
            style={{ ...fieldStyle, width: '200px', textTransform: 'uppercase' }}
          />
          <input
            aria-label="County state"
            value={stateInput}
            maxLength={2}
            onChange={(e): void => setStateInput(e.target.value)}
            placeholder="ST"
            style={{ ...fieldStyle, width: '64px', textTransform: 'uppercase' }}
          />
          <button type="button" disabled={update.isPending} onClick={save} style={linkButton}>
            Save
          </button>
          <button
            type="button"
            onClick={(): void => {
              setError(null);
              setEditing(false);
            }}
            style={{ ...linkButton, color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
        </div>
        {error !== null && (
          <p role="alert" style={{ ...subText, color: 'var(--color-danger)', margin: '4px 0 0' }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={subText}>County</span>
        <span style={{ fontWeight: 600, fontSize: '13px' }}>
          {county !== null ? `${county}, ${countyState ?? ''}` : 'Not set'}
        </span>
        {countySource === 'USER' && <Pill label="Typed" tone="accent" />}
        {countySource === 'DERIVED' && <Pill label="Filled from GPS" tone="muted" />}
        {canManage && (
          <button type="button" onClick={startEdit} style={linkButton}>
            Edit county
          </button>
        )}
        {canManage && countySource === 'USER' && (
          <button
            type="button"
            disabled={update.isPending}
            onClick={clearTyped}
            style={{ ...linkButton, color: 'var(--color-danger)' }}
          >
            Clear typed county
          </button>
        )}
      </div>
      {county === null && (
        <p style={{ ...subText, margin: '4px 0 0' }}>
          Filled automatically from the site&apos;s GPS pin in OK and TX
        </p>
      )}
    </div>
  );
}
